import React from "react";
import Head from "next/head";
import { Field, Formik } from "formik";
import { Row, Col, Form, Label, Container, Button, Spinner, FormGroup, Input, FormText } from 'reactstrap';
import * as Yup from 'yup';
import { InputField } from "../components/InputField";
import Layout from "../components/Layout";
import { CreateNewEventRequestComponent, CreateNewEventRequestInput } from "../generated/apolloComponents";
import { withApollo } from "../lib/apollo";
import headTitle from "../components/ui/HeadTitle";

const AddEventSchema = Yup.object().shape({
  email: Yup.string().required('E-mail is required').email('Valid e-mail address is required'),
  company: Yup.string().required('Company is required'),
  listingType: Yup.string().required('Listing type is required'),
  partnersLink: Yup.string().when('listingType', {
    is: (listingType) => listingType == 'free',
    then: Yup.string().required('Link is required')
  }),
  url: Yup.string().required('Event website address is required'),
});

const AddEventPage = () => {
  const title = headTitle('Add Event');
  const description = 'Add aritificial intelligence and machine learning event to curated list.'
  return (
    <>
      <Head>
        <title>{title}</title>
        <meta key="og:title" property="og:title" content={title} />
        <meta key="og:url" property="og:url" content={`https://aiml.events/add-event`} />
        <meta key="og:image" property="og:image" content="https://aiml.events/static/img/banner.png" />
        <meta key="og:description" property="og:description" content={description} />
        <meta name="description" content={description} />
      </Head>
      <Layout>
      <main>
        <div className="py-4 bg-light">
          <Container>
            <div className="mb-3">
              <h2>Add Event</h2>
            </div>
            <CreateNewEventRequestComponent>
              {createNewEventRequest => (
                <Formik
                  onSubmit={async (data, { setSubmitting, setFieldValue }) => {
                    let mutationData: CreateNewEventRequestInput = { 
                      url: data.url,
                      email: data.email,
                      company: data.company,
                      listingType: data.listingType,
                      partnersLink: data.partnersLink,
                     };
                    await createNewEventRequest({
                      variables: {
                        input: {
                          ...mutationData
                        }
                      }
                    });
                    setSubmitting(false);
                    setFieldValue('sent', true);
                  }}
                  initialValues={{
                    url: '',
                    company: '',
                    email: '',
                    listingType: '',
                    partnersLink: '',
                    sent: false,
                  }}
                  validationSchema={AddEventSchema}
                  >
                  {({ values, handleSubmit, isSubmitting, errors }) => (
                    <>
                    {values.sent && <>
                      <h1>Request sent. Thank you!</h1>
                      <h3>We will get in touch soon.</h3>
                    </>}
                    {!values.sent && <Form onSubmit={handleSubmit}>
                      <p>Are you organizing an event focused on artificial intelligence, machine learning, or data? Tell us about your event and we will add it to our website. </p>
                      <Row>
                        <Col className="mb-4">
                          <Label for="url">Event website address</Label>
                          <Field name="url" placeholder="Event website address" component={InputField} className="form-control" />
                        </Col>
                      </Row>
                      <Row>
                        <Col className="mb-4">
                          <Label for="email">Your e-mail</Label>
                          <Field name="email" placeholder="Your e-mail" component={InputField} className="form-control" />
                        </Col>
                      </Row>
                      <Row>
                        <Col className="mb-4">
                          <Label for="company">Your company name</Label>
                          <Field name="company" placeholder="Your company name" component={InputField} className="form-control" />
                        </Col>
                      </Row>
                      <Row>
                        <Col className="mb-4">
                          <Label>Listing type</Label>
                          <FormGroup check className="mb-4">
                            <Field name="listingType" value="free" type="radio">
                              {({ field, form: { touched, errors }, ...props }) => (<Input name="listingType" type="radio" value="free" invalid={!!errors["listingType"]} id="listingTypeFree" {...field} {...props} />)}
                            </Field>
                            <Label check for="listingTypeFree">With media partnership (Free)</Label>
                            <FormText className="mb-3">AI &amp; ML Events will be listed on your event website and other material as a media partner. See details at <a href="/media-partnership" target="_blank">Media Partnership</a>.</FormText>
                            <Field name="listingType" value="paid" type="radio">
                              {({ field, form: { touched, errors }, ...props }) => (<Input name="listingType" type="radio" value="paid" invalid={!!errors["listingType"]} id="listingTypePaid" {...field} {...props} />)}
                            </Field>
                            <Label check for="listingTypePaid">Without media partnership (&euro;199)</Label>
                            <FormText>You have no obligation to mention AI &amp; ML Events at your event. Payment link will be sent via email.</FormText>
                          </FormGroup>
                          {errors["listingType"] && <div className="small text-danger">{errors["listingType"]}</div>}
                        </Col>
                      </Row>
                      {values['listingType'] == 'free' && <Row>
                        <Col className="mb-4">
                          <Label for="partnersLink">Link to your event website page where you have mentioned AI &amp; ML Events as a media partner, according to the <a href="/media-partnership" target="_blank">Media Partnership guidelines</a>. If this page does not list AI &amp; ML Events as a partner, your event submission will be rejected.</Label>
                          <Field name="partnersLink" placeholder="Event media partners link" component={InputField} className="form-control" />
                        </Col>
                      </Row>}
                      <Row>
                        <Col>
                          <Button type="submit" color="primary" disabled={isSubmitting}>
                            {isSubmitting && <><Spinner size="sm" /> Sending...</>}
                            {!isSubmitting && <>Submit</>}
                          </Button>
                        </Col>
                      </Row>
                    </Form>}
                    </>
                  )}
                </Formik>
              )}</CreateNewEventRequestComponent>
          </Container>
        </div>
      </main>
    </Layout>
  </>
  );
};

export default withApollo(AddEventPage);
